import LoadingView from "legacy/LoadingView"
import AvailabilityDailyModal from "legacy/availability/AvailabilityDailyModal"
import {
  capitalizeFirstLetter,
  inchesToFeet,
  inchesToSquareFeet,
  numberWithDelimiter,
} from "legacy/helpers.es6.jsx"
import PropTypes from "prop-types"
import React from "react"

class AvailabilitySummary extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      loading: true,
      occupancies: null,
      availabilityByCategory: null,
      hasSpaceGroups: true,
    }
  }

  static getInventoryTotal(spaceGroup) {
    if (spaceGroup.type === "SpaceGroup::SquareFootage") {
      return inchesToSquareFeet(spaceGroup.inventory_total)
    } else if (spaceGroup.type.includes("SpaceGroup::Linear")) {
      return inchesToFeet(spaceGroup.inventory_total)
    } else {
      return spaceGroup.inventory_total
    }
  }

  static getOccupanciesTotal(occupancies) {
    const nightValues = Object.values(occupancies.nights)
    if (!nightValues || nightValues.length === 0) {
      return 0
    }

    const firstNight = nightValues[0]
    if (!firstNight) {
      return 0
    }

    if (occupancies.type === "SpaceGroup::SquareFootage") {
      return inchesToSquareFeet(firstNight.square_inches || 0)
    } else if (occupancies.type.includes("SpaceGroup::Linear")) {
      return inchesToFeet(firstNight.loa || 0)
    } else {
      return firstNight.count || 0
    }
  }

  static getSpaceGroupUnitSymbol(spaceGroup) {
    switch (spaceGroup.type) {
      case "SpaceGroup::SquareFootage":
        return "sqft"
      case "SpaceGroup::Linear":
      case "SpaceGroup::LinearV2":
        return "'"
      default:
        return ""
    }
  }

  componentDidMount() {
    this.fetchData()
  }

  availabilityParams() {
    return {
      check_in_date: this.props.night,
      check_out_date: this.props.night,
    }
  }

  fetchData() {
    $.post(this.props.dataRoute, this.availabilityParams(), (data) => {
      this.setState({
        loading: false,
        occupancies: data.occupancies,
        hasSpaceGroups: data.has_space_groups,
        availabilityByCategory: this.availabilityByCategory(data),
      })
    }).fail(() => {
      alert("An error occurred while trying to calculate availability.")
      this.setState({ loading: false })
    })
  }

  availabilityByCategory(data) {
    // Since occupancies is an object, we need to use Object.values
    // to convert it to an array before using reduce
    return Object.values(data.occupancies).reduce((groups, occupancy) => {
      const dockType = occupancy.dock_type.toLowerCase()
      let category
      switch (dockType) {
        case "dock":
          category = "dockage"
          break
        case "mooring":
          category = "moorage"
          break
        case "storage":
          category = "storage"
          break
        default:
          category = "all"
      }

      if (!groups[category]) {
        groups[category] = []
      }
      groups[category].push(occupancy)
      return groups
    }, {})
  }

  categoryProgressCalculations(data) {
    const totalAvailableInventory = data.reduce(
      (sum, datum) => sum + AvailabilitySummary.getInventoryTotal(datum),
      0
    )
    const occupancyAmount = data.reduce(
      (sum, datum) => sum + AvailabilitySummary.getOccupanciesTotal(datum),
      0
    )
    const remainingAvailability = Math.floor(
      totalAvailableInventory - occupancyAmount
    )
    const occupancyPercentage = Math.round(
      (occupancyAmount / totalAvailableInventory) * 100
    )
    const progressWidthPercentage = Math.min(occupancyPercentage, 100)
    let dataType = data.map((datum) => datum.type)[0]
    let unitSymbol = ""

    if (["SpaceGroup::Linear", "SpaceGroup::LinearV2"].includes(dataType)) {
      dataType = "Linear"
      unitSymbol = "'"
    } else if (dataType === "SpaceGroup::Count") {
      dataType = "Count"
    } else {
      dataType = "Square Feet"
      unitSymbol = "sqft"
    }

    return {
      dataType,
      percentage: occupancyPercentage,
      widthPercentage: progressWidthPercentage,
      remaining: remainingAvailability,
      unitSymbol,
    }
  }

  categoryProgress(category, data) {
    const total = data.reduce(
      (sum, datum) => sum + AvailabilitySummary.getInventoryTotal(datum),
      0
    )

    if (total !== 0) {
      const countArray = []
      const linearArray = []
      const sqftArray = []

      data.forEach((datum) => {
        if (datum.type === "SpaceGroup::Count") {
          countArray.push(datum)
        } else if (
          ["SpaceGroup::Linear", "SpaceGroup::LinearV2"].includes(datum.type)
        ) {
          linearArray.push(datum)
        } else {
          sqftArray.push(datum)
        }
      })

      const dataArray = [countArray, linearArray, sqftArray]

      return (
        <div
          key={`availability-summary-${category.replace(" ", "-")}`}
          className="mb-2 flex w-full justify-evenly xl:mb-0 xl:flex-row"
        >
          <div className="mx-2 w-full rounded bg-gray-200 p-4">
            <p className="text-left text-xl">
              {capitalizeFirstLetter(category)}
            </p>
            <div className="flex w-full flex-col">
              {dataArray.map(
                (data, index) =>
                  data.length > 0 &&
                  this.renderProgressBar(
                    this.categoryProgressCalculations(data),
                    `${category}-${index}`
                  )
              )}
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  spaceGroupDataExists() {
    return (
      this.state.occupancies && Object.keys(this.state.occupancies).length > 0
    )
  }

  renderProgressBar(data, key) {
    return (
      <div key={key} className="mb-2 w-full">
        <div className="flex w-full flex-row justify-between">
          <p className="font-semibold">
            {data.dataType}
            {data.dataType === "Linear" ? (
              <i
                className="fa-kit fa-info ml-1 mt-0.5 cursor-pointer text-xs text-gray-500"
                data-container="body"
                data-toggle="popover"
                data-placement="top"
                data-content="Buffer is included in the calculation for linear dockage."
              />
            ) : null}
          </p>
          <p className="text-muted">{`${numberWithDelimiter(data.remaining)}${
            data.unitSymbol === "sqft" ? ` ${data.unitSymbol}` : data.unitSymbol
          } remaining`}</p>
        </div>
        <div className="progress my-0 h-2 rounded bg-white">
          <div
            className="progress-bar rounded bg-blue-600"
            role="progressbar"
            aria-valuenow={data.percentage}
            aria-valuemin="0"
            aria-valuemax="100"
            style={{
              width: `${data.widthPercentage}%`,
              fontWeight: 600,
            }}
          />
        </div>
      </div>
    )
  }

  summaryByCategory() {
    if (this.spaceGroupDataExists()) {
      return (
        <div className="mb-3 flex flex-col px-2 xl:flex-row">
          {Object.entries(this.state.availabilityByCategory).map(
            ([category, data]) => this.categoryProgress(category, data)
          )}
        </div>
      )
    } else {
      return (
        <div>
          <div className="mb-3">
            <p className="text-center font-medium">
              Set up Space Groups to gain more insight into your marina&apos;s
              availability.
            </p>
            <div className="flex justify-center">
              <a
                className="btn btn-secondary"
                href={this.props.spaceGroupsRoute}
              >
                Set Up Space Groups
              </a>
            </div>
          </div>
        </div>
      )
    }
  }

  render() {
    if (this.state.loading) {
      return <LoadingView />
    } else {
      return (
        <div className="hidden-print flex flex-col">
          <div className="mb-6 flex w-full flex-row justify-between bg-gray-200 p-5 text-left">
            <div className="flex flex-row">
              <h4>Availability</h4>
            </div>
            {this.spaceGroupDataExists() ? (
              <AvailabilityDailyModal
                night={this.props.night}
                todayRoute={this.props.todayRoute}
                availabilityByCategory={this.state.availabilityByCategory}
                marinaMcomId={this.props.marinaMcomId}
                marinaName={this.props.marinaName}
                showingTonightsBoats={this.props.showingTonightsBoats}
              />
            ) : null}
          </div>
          {this.summaryByCategory()}
        </div>
      )
    }
  }
}

AvailabilitySummary.propTypes = {
  night: PropTypes.string.isRequired,
  dataRoute: PropTypes.string.isRequired,
  todayRoute: PropTypes.string.isRequired,
  spaceGroupsRoute: PropTypes.string.isRequired,
  marinaMcomId: PropTypes.string,
  marinaName: PropTypes.string,
  showingTonightsBoats: PropTypes.bool,
}

export default AvailabilitySummary
